import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Section from '../components/ui/Section';
import SEOHelmet from '../seo/SEOHelmet';
import HeaderBanner from '../components/ui/HeaderBanner';
import { useLanguage } from '../i18n/LanguageContext';

const ContentContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const Title = styled(motion.h1)`
  font-size: 2.8rem;
  margin-bottom: 3rem;
  text-align: center;
  position: relative;
  display: inline-block;
  left: 50%;
  transform: translateX(-50%);
  
  &:after {
    content: '';
    position: absolute;
    bottom: -20px;
    left: 0;
    width: 100px;
    height: 3px;
    background: linear-gradient(90deg, var(--accent-primary), transparent);
    left: 50%;
    transform: translateX(-50%);
  }
`;

const Heading = styled(motion.h2)`
  font-size: 1.8rem;
  margin: 2.5rem 0 1.5rem;
  position: relative;
  padding-bottom: 0.5rem;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 60px;
    height: 2px;
    background-color: var(--accent-primary);
  }
`;

const SubHeading = styled(motion.h3)`
  font-size: 1.4rem;
  margin: 1.5rem 0 1rem;
  color: var(--text-primary);
`;

const Paragraph = styled(motion.p)`
  margin-bottom: 1.5rem;
  line-height: 1.8;
  color: var(--text-secondary);
  font-size: 1.05rem;
`;

const List = styled(motion.ul)`
  margin-bottom: 1.5rem;
  padding-left: 1.5rem;
`;

const ListItem = styled(motion.li)`
  margin-bottom: 0.8rem;
  line-height: 1.6;
  color: var(--text-secondary);
`;

const LastUpdated = styled.p`
  margin-top: 4rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--text-secondary);
  font-style: italic;
  font-size: 0.9rem;
`;

const CookiePolicy: React.FC = () => {
  const { language } = useLanguage();
  
  const isItalian = language === 'it';
  const lastUpdated = isItalian ? 'Ultimo aggiornamento: 20 Luglio 2023' : 'Last updated: July 20, 2023';
  
  return (
    <>
      <SEOHelmet
        title={isItalian ? "Informativa sui Cookie - J-O-H" : "Cookie Policy - J-O-H"}
        description={isItalian 
          ? "Informativa sui Cookie di J-O-H Gestione Immobiliare di Lusso. Come utilizziamo i cookie sul nostro sito web." 
          : "Cookie Policy of J-O-H Luxury Property Management. How we use cookies on our website."}
        canonical="/cookie-policy"
      />
      
      <HeaderBanner />
      
      <Section padding="5rem 0">
        <ContentContainer>
          <Title
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {isItalian ? "Informativa sui Cookie" : "Cookie Policy"}
          </Title>
          
          <Paragraph>
            {isItalian 
              ? "Questa Informativa sui Cookie spiega cosa sono i cookie e come li utilizziamo. Dovresti leggere questa informativa per comprendere cosa sono i cookie, come li utilizziamo, i tipi di cookie che utilizziamo, le informazioni che raccogliamo utilizzando i cookie e come queste informazioni vengono utilizzate, e come controllare le preferenze dei cookie."
              : "This Cookie Policy explains what cookies are and how we use them. You should read this policy to understand what cookies are, how we use them, the types of cookies we use, the information we collect using cookies, and how that information is used, and how to control your cookie preferences."}
          </Paragraph>
          
          <Heading>
            {isItalian ? "1. Cosa Sono i Cookie" : "1. What Are Cookies"}
          </Heading>
          <Paragraph>
            {isItalian 
              ? "I cookie sono piccoli file di testo che vengono archiviati sul tuo computer o dispositivo mobile quando visiti un sito web. I cookie vengono ampiamente utilizzati dai proprietari di siti web per far funzionare i loro siti web in modo efficiente e fornire informazioni analitiche."
              : "Cookies are small text files that are stored on your computer or mobile device when you visit a website. Cookies are widely used by website owners to make their websites work efficiently and to provide analytical information."}
          </Paragraph>
          
          <Heading>
            {isItalian ? "2. Come Utilizziamo i Cookie" : "2. How We Use Cookies"}
          </Heading>
          <Paragraph>
            {isItalian 
              ? "Utilizziamo i cookie per diversi motivi, dettagliati di seguito. Sfortunatamente, nella maggior parte dei casi non esistono opzioni standard del settore per disabilitare i cookie senza disabilitare completamente le funzionalità e le caratteristiche che aggiungono a questo sito. Si consiglia di lasciare attivi tutti i cookie se non si è sicuri di averne bisogno o meno."
              : "We use cookies for a variety of reasons detailed below. Unfortunately, in most cases, there are no industry standard options for disabling cookies without completely disabling the functionality and features they add to this site. It is recommended that you leave on all cookies if you are not sure whether you need them or not."}
          </Paragraph>
          
          <Heading>
            {isItalian ? "3. Tipi di Cookie che Utilizziamo" : "3. Types of Cookies We Use"}
          </Heading>
          
          <SubHeading>
            {isItalian ? "3.1 Cookie Necessari" : "3.1 Necessary Cookies"}
          </SubHeading>
          <Paragraph>
            {isItalian 
              ? "I cookie necessari sono essenziali per il corretto funzionamento del sito web. Questi cookie garantiscono le funzionalità di base e le caratteristiche di sicurezza del sito web, in modo anonimo."
              : "Necessary cookies are essential for the proper functioning of the website. These cookies ensure basic functionalities and security features of the website, anonymously."}
          </Paragraph>
          
          <SubHeading>
            {isItalian ? "3.2 Cookie Funzionali" : "3.2 Functional Cookies"}
          </SubHeading>
          <Paragraph>
            {isItalian 
              ? "I cookie funzionali aiutano a eseguire alcune funzionalità come la condivisione del contenuto del sito web su piattaforme di social media, la raccolta di feedback e altre funzionalità di terze parti."
              : "Functional cookies help to perform certain functionalities like sharing the content of the website on social media platforms, collecting feedback, and other third-party features."}
          </Paragraph>
          
          <SubHeading>
            {isItalian ? "3.3 Cookie di Performance" : "3.3 Performance Cookies"}
          </SubHeading>
          <Paragraph>
            {isItalian 
              ? "I cookie di performance vengono utilizzati per comprendere e analizzare gli indici di prestazione chiave del sito web per aiutare a fornire una migliore esperienza utente ai visitatori."
              : "Performance cookies are used to understand and analyze the key performance indexes of the website which helps in delivering a better user experience for the visitors."}
          </Paragraph>
          
          <SubHeading>
            {isItalian ? "3.4 Cookie Analitici" : "3.4 Analytical Cookies"}
          </SubHeading>
          <Paragraph>
            {isItalian 
              ? "I cookie analitici vengono utilizzati per comprendere come i visitatori interagiscono con il sito web. Questi cookie aiutano a fornire informazioni sulle metriche del numero di visitatori, della frequenza di rimbalzo, della fonte di traffico, ecc."
              : "Analytical cookies are used to understand how visitors interact with the website. These cookies help provide information on metrics the number of visitors, bounce rate, traffic source, etc."}
          </Paragraph>
          
          <SubHeading>
            {isItalian ? "3.5 Cookie Pubblicitari" : "3.5 Advertisement Cookies"}
          </SubHeading>
          <Paragraph>
            {isItalian 
              ? "I cookie pubblicitari vengono utilizzati per fornire ai visitatori annunci e campagne di marketing pertinenti. Questi cookie tracciano i visitatori attraverso i siti web e raccolgono informazioni per fornire annunci personalizzati."
              : "Advertisement cookies are used to provide visitors with relevant ads and marketing campaigns. These cookies track visitors across websites and collect information to provide customized ads."}
          </Paragraph>
          
          <Heading>
            {isItalian ? "4. Cookie di Terze Parti" : "4. Third-Party Cookies"}
          </Heading>
          <Paragraph>
            {isItalian 
              ? "In alcuni casi particolari utilizziamo anche cookie forniti da terze parti fidate. I dettagli della sezione seguente specificano quali cookie di terze parti potresti incontrare attraverso questo sito."
              : "In some special cases, we also use cookies provided by trusted third parties. The following section details which third party cookies you might encounter through this site."}
          </Paragraph>
          <List>
            <ListItem>
              {isItalian 
                ? "Questo sito utilizza Google Analytics, uno dei più diffusi e affidabili strumenti di analisi sul web, per aiutarci a capire come usi il sito e come possiamo migliorare la tua esperienza. Questi cookie possono tracciare cose come quanto tempo trascorri sul sito e le pagine che visiti, in modo che possiamo continuare a produrre contenuti coinvolgenti."
                : "This site uses Google Analytics which is one of the most widespread and trusted analytics solutions on the web to help us understand how you use the site and how we can improve your experience. These cookies may track things such as how long you spend on the site and the pages that you visit so we can continue to produce engaging content."}
            </ListItem>
            <ListItem>
              {isItalian 
                ? "Testiamo regolarmente nuove funzionalità e apportiamo modifiche sottili al modo in cui il sito viene fornito. Quando stiamo ancora testando nuove funzionalità, questi cookie possono essere utilizzati per garantire che tu riceva un'esperienza coerente mentre sei sul sito."
                : "We regularly test new features and make subtle changes to the way that the site is delivered. When we are still testing new features, these cookies may be used to ensure that you receive a consistent experience whilst on the site."}
            </ListItem>
          </List>
          
          <Heading>
            {isItalian ? "5. Gestione delle tue Preferenze sui Cookie" : "5. Managing Your Cookie Preferences"}
          </Heading>
          <Paragraph>
            {isItalian 
              ? "Puoi gestire le tue preferenze sui cookie attraverso il nostro banner sui cookie che appare quando visiti il nostro sito per la prima volta. Puoi scegliere di accettare o rifiutare tutti i cookie o selezionare quali categorie di cookie desideri accettare."
              : "You can manage your cookie preferences through our cookie banner that appears when you first visit our site. You can choose to accept or decline all cookies, or select which categories of cookies you wish to accept."}
          </Paragraph>
          <Paragraph>
            {isItalian 
              ? "Inoltre, la maggior parte dei browser web consente un certo controllo della maggior parte dei cookie attraverso le impostazioni del browser. Per saperne di più su come modificare le impostazioni del browser o come bloccare, gestire o filtrare i cookie, visita il sito web del tuo browser:"
              : "Additionally, most web browsers allow some control of most cookies through the browser settings. To find out more about how to change your browser settings or how to block, manage or filter cookies, please visit your browser's website:"}
          </Paragraph>
          <List>
            <ListItem>Chrome: chrome://settings/content/cookies</ListItem>
            <ListItem>Safari: https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac</ListItem>
            <ListItem>Firefox: about:preferences#privacy</ListItem>
            <ListItem>Edge: edge://settings/content/cookies</ListItem>
          </List>
          
          <Heading>
            {isItalian ? "6. Cosa Succede Se Disabiliti i Cookie" : "6. What Happens If You Disable Cookies"}
          </Heading>
          <Paragraph>
            {isItalian 
              ? "Se decidi di disabilitare i cookie, purtroppo non saremo in grado di salvare le tue preferenze. Ciò significa che ogni volta che visiti questo sito web dovrai abilitare o disabilitare nuovamente i cookie. Inoltre, alcune funzionalità del sito potrebbero non funzionare correttamente."
              : "If you decide to disable cookies, unfortunately we will not be able to save your preferences. This means that every time you visit this website you will need to enable or disable cookies again. Additionally, some functionalities of the site may not work properly."}
          </Paragraph>
          
          <Heading>
            {isItalian ? "7. Ulteriori Informazioni" : "7. More Information"}
          </Heading>
          <Paragraph>
            {isItalian 
              ? "Speriamo che questo abbia chiarito le cose per te e, come accennato in precedenza, se c'è qualcosa di cui non sei sicuro, è generalmente più sicuro lasciare i cookie abilitati nel caso in cui interagiscano con una delle funzionalità che utilizzi sul nostro sito."
              : "Hopefully that has clarified things for you and as was previously mentioned if there is something that you aren't sure whether you need or not it's usually safer to leave cookies enabled in case it does interact with one of the features you use on our site."}
          </Paragraph>
          <Paragraph>
            {isItalian 
              ? "Se hai ancora domande sulla nostra Informativa sui Cookie, non esitare a contattarci tramite la nostra pagina di contatto."
              : "If you are still looking for more information, please contact us through our contact page."}
          </Paragraph>
          
          <LastUpdated>{lastUpdated}</LastUpdated>
        </ContentContainer>
      </Section>
    </>
  );
};

export default CookiePolicy; 